/**
 * Walking with the guard up (lane C0-POSE-FIX): which frame of the `guardWalk` loop (motions/guardWalk.ts) a figure
 * that is guarding and moving shows, and which way it faces.
 *
 * The rules turn a moving figure toward the way it walks, guarding or not (shared world.ts `moveEntity`); in a fight
 * that read as turning the back on the foe. While guarding with a living target near, the figure is drawn facing the
 * target instead (a strafe), stepping forward when it closes in and the loop played backwards when it gives ground
 * (the front foot stays in front). No rule changes: the facing is drawn only.
 */
import { screenHeading, type Entity } from '@nine/shared';
import type { IsoCamera } from './camera';
import { motionPhase } from './motions';
import { strideFps } from './qinggongAnim';
import { FRAMES, viewOf, type SpriteSet } from './sprites';

/** Farthest a target is faced from while guarding on the move (world units). */
const FACE_RANGE = 12;
/**
 * The guarded shuffle against the walk's stride rate: its planted foot slides about 0.38 px a frame against the walk's
 * 1.43, and it moves at 0.45 of the walk's speed, so it steps 1.7 times as often to keep the feet planted (review R2).
 */
const RATE = 1.7;

export interface GuardWalkShot {
  img: HTMLCanvasElement;
  facing: number;
}

/**
 * The guarded walk's frame for `e` (guarding and moving), or null for figures without the rig. `set` and `facing` are
 * the view the renderer picked from the walking heading; `base` is the figure's own set (all its views).
 */
export function guardWalkShot(
  e: Entity,
  set: SpriteSet,
  facing: number,
  base: SpriteSet | undefined,
  iso: IsoCamera,
  target: Entity | null | undefined,
): GuardWalkShot | null {
  let view = { set, facing };
  let back = false;
  if (base && target && !target.dead && target !== e) {
    const dx = target.x - e.x;
    const dy = target.y - e.y;
    const l = Math.hypot(dx, dy);
    if (l > 0.01 && l < FACE_RANGE) {
      const h = iso.heading({ heading: screenHeading(dx, dy), dirX: dx / l, dirY: dy / l });
      view = viewOf(base, h, facing);
      // Giving ground: walking away from the one faced.
      const mx = e.dirX ?? 0;
      const my = e.dirY ?? 0;
      back = (mx * dx + my * dy) / l < -0.35;
    }
  }
  if (!view.set.extra) return null;
  const f = Math.abs(Math.floor(e.animT * strideFps(e, view.set) * RATE)) % FRAMES;
  const ph = motionPhase('guardWalk', back ? FRAMES - 1 - f : f);
  const img = view.set.extra('motion', ph, false, () => view.set.block);
  return { img, facing: view.facing };
}
